import * as React from "react"
import { useIsMobile } from "@/hooks/use-mobile"

const SIDEBAR_COOKIE_NAME = "sidebar:state"
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7
const SIDEBAR_KEYBOARD_SHORTCUT = "b"

type SidebarContext = {
  state: "expanded" | "collapsed"
  open: boolean
  setOpen: (open: boolean) => void
  openMobile: boolean
  setOpenMobile: (open: boolean) => void
  isMobile: boolean
  toggleSidebar: () => void
}

const SidebarContext = React.createContext<SidebarContext | null>(null)

function useSidebar() {
  const context = React.useContext(SidebarContext)
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider.")
  }

  return context
}

interface SidebarProviderProps {
  defaultOpen?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
  className?: string
  style?: React.CSSProperties
  children: React.ReactNode
}

const SidebarProvider = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & SidebarProviderProps
>(
  (
    {
      defaultOpen = true,
      open: openProp,
      onOpenChange: setOpenProp,
      className,
      style,
      children,
      ...props
    },
    ref
  ) => {
    const isMobile = useIsMobile()
    const [openMobile, setOpenMobile] = React.useState(false)

    // Handle body scroll locking when mobile sidebar is open with improved animation
    React.useEffect(() => {
      if (isMobile && openMobile) {
        // Prevent background scrolling when mobile sidebar is open
        document.body.style.overflow = 'hidden';
        document.body.style.touchAction = 'none';
      } else {
        document.body.style.overflow = '';
        document.body.style.touchAction = '';
      }
      
      return () => {
        document.body.style.overflow = '';
        document.body.style.touchAction = '';
      };
    }, [isMobile, openMobile]);

    // Get initial sidebar state from localStorage with improved persistence
    const getInitialSidebarState = React.useCallback(() => {
      if (typeof window === 'undefined') return defaultOpen;
      
      try {
        // First try to get from localStorage (more reliable than cookies)
        const localStorageValue = localStorage.getItem(SIDEBAR_COOKIE_NAME);
        if (localStorageValue !== null) {
          return localStorageValue === 'true';
        }
        
        // Fallback to cookie for backwards compatibility
        const cookieValue = document.cookie
          .split('; ')
          .find(row => row.startsWith(SIDEBAR_COOKIE_NAME))
          ?.split('=')[1];
        
        if (cookieValue !== undefined) {
          return cookieValue === 'true';
        }
      } catch (error) {
        console.warn('Error reading sidebar state from storage:', error);
      }
      
      return defaultOpen;
    }, [defaultOpen]);

    // This is the internal state of the sidebar.
    // We use openProp and setOpenProp for control from outside the component.
    const [_open, _setOpen] = React.useState(() => getInitialSidebarState());
    
    const open = openProp ?? _open
    const setOpen = React.useCallback(
      (value: boolean | ((value: boolean) => boolean)) => {
        const openState = typeof value === "function" ? value(open) : value
        if (setOpenProp) {
          setOpenProp(openState)
        } else {
          _setOpen(openState)
        }

        // Save to both localStorage and cookie for maximum compatibility
        try {
          localStorage.setItem(SIDEBAR_COOKIE_NAME, openState.toString());
          console.log('Sidebar preference saved to localStorage:', openState);
        } catch (error) {
          console.warn('Error saving sidebar state to localStorage:', error);
        }

        // This sets the cookie to keep the sidebar state as fallback
        try {
          document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
        } catch (error) {
          console.warn('Error saving sidebar state to cookie:', error);
        }
      },
      [setOpenProp, open]
    )

    // Helper to toggle the sidebar with improved logic for mobile
    const toggleSidebar = React.useCallback(() => {
      if (isMobile) {
        setOpenMobile((open) => !open)
      } else {
        setOpen((open) => !open)
      }
    }, [isMobile, setOpen, setOpenMobile])

    // Adds a keyboard shortcut to toggle the sidebar.
    React.useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (
          event.key === SIDEBAR_KEYBOARD_SHORTCUT &&
          (event.metaKey || event.ctrlKey)
        ) {
          event.preventDefault()
          toggleSidebar()
        }
      }

      window.addEventListener("keydown", handleKeyDown)
      return () => window.removeEventListener("keydown", handleKeyDown)
    }, [toggleSidebar])

    // Enhanced route change detection to close sidebar on mobile
    React.useEffect(() => {
      if (isMobile && openMobile) {
        const handleRouteChange = () => {
          setOpenMobile(false);
        };
        
        window.addEventListener('popstate', handleRouteChange);
        
        // For single page app navigation
        const originalPushState = window.history.pushState;
        window.history.pushState = function() {
          originalPushState.apply(this, arguments);
          handleRouteChange();
        };
        
        return () => {
          window.removeEventListener('popstate', handleRouteChange);
          window.history.pushState = originalPushState;
        };
      }
    }, [isMobile, openMobile, setOpenMobile]);

    // We add a state so that we can do data-state="expanded" or "collapsed".
    // This makes it easier to style the sidebar with Tailwind classes.
    const state = open ? "expanded" : "collapsed"

    const contextValue = React.useMemo<SidebarContext>(
      () => ({
        state,
        open,
        setOpen,
        isMobile,
        openMobile,
        setOpenMobile,
        toggleSidebar,
      }),
      [state, open, setOpen, isMobile, openMobile, setOpenMobile, toggleSidebar]
    )

    return (
      <SidebarContext.Provider value={contextValue}>
        <div
          style={
            {
              "--sidebar-width": "16rem",
              "--sidebar-width-icon": "3rem",
              ...style,
            } as React.CSSProperties
          }
          className={className}
          ref={ref}
          {...props}
        >
          {children}
        </div>
      </SidebarContext.Provider>
    )
  }
)
SidebarProvider.displayName = "SidebarProvider"

export { SidebarContext, SidebarProvider, useSidebar }
export { SIDEBAR_KEYBOARD_SHORTCUT }
